/**
 * Methodology Translations Module (French)
 * Research Approach & Data Sources for JCI Development Councils Youth Futures Observatory
 * @module methodology
 * @language fr
 */

export const methodology = {
  methodology: {
    // Page Meta
    pageTitle: "Approche de Recherche et Sources de Données",
    heroSubtitle: "Intelligence basée sur les preuves habilitant les Organisations Nationales et Locales JCI",
    heroDescription: "L'Observatoire des Futurs de la Jeunesse des Conseils de Développement JCI est construit sur une méthodologie rigoureuse et auditable qui combine des données vérifiées de l'organisation JCI, des initiatives mondiales de recherche sur la jeunesse et des indices économiques établis.",
    
    // Hero CTAs
    ctaExplore: "Explorer la Méthodologie",
    ctaContact: "Contacter l'Équipe",
    
    // Executive Overview
    executiveOverviewTitle: "Résumé Exécutif",
    executiveOverviewText: "Ce document décrit l'approche de recherche, les sources de données et les méthodes analytiques utilisées pour créer les insights de l'observatoire. Chaque statistique affichée est traçable directement à des sources vérifiées et auditables.",
    coreCommitment: "Chaque statistique affichée est traçable directement à des sources vérifiées et auditables.",
    
    // Overview Section
    overviewTitle: "Le Framework DASHCity",
    overviewDescription: "Une approche complète en 5 phases conçue pour transformer les organisations en puissances à fort impact grâce à une évaluation systématique, une mesure et une amélioration continue.",
    overviewIntro: "DASHCity fournit un parcours structuré permettant aux organisations d'identifier les opportunités, de mesurer les performances, de communiquer la valeur, d'établir la gouvernance et d'étendre leur impact à l'échelle mondiale.",
    frameworkFeatures: "Framework en 5 Phases DASHCity",
    inclusiveFeature: "Inclusif - Conçu pour les organisations de toutes tailles et régions",
    scalableFeature: "Évolutif - De l'implémentation locale à mondiale",
    
    // Framework Characteristics
    characteristicsTitle: "Principes du Framework",
    characteristics: {
      evidenceBased: "Basé sur des Preuves",
      evidenceBasedDesc: "Fondé sur des données vérifiées et la recherche",
      iterative: "Itératif",
      iterativeDesc: "Amélioration continue par des boucles de rétroaction",
      inclusive: "Inclusif",
      inclusiveDesc: "Conçu pour les conseils de toutes tailles et régions",
      scalable: "Évolutif",
      scalableDesc: "Des organisations locales à l'implémentation mondiale",
      transparent: "Transparent",
      transparentDesc: "Méthodologie ouverte et standards de données",
      actionable: "Actionnable",
      actionableDesc: "Outils pratiques et livrables clairs"
    },
    
    // Phase Status Badges
    statusDone: "TERMINÉ",
    statusDoing: "EN COURS",
    statusToDo: "PLANIFIÉ",
    
    // Phase Detail Labels
    activitiesLabel: "Activités",
    deliverablesLabel: "Livrables",
    resultLabel: "Résultat",
    
    // Section Titles
    dataCollectionTitle: "Méthodologie de Collecte de Données",
    
    // Phase 1: IDENTIFY
    phase1Title: "IDENTIFIER",
    phase1Period: "Septembre - Novembre 2025",
    phase1Objective: "Cartographier l'écosystème complet de développement du leadership jeunesse",
    phase1Description: "La phase de fondation se concentre sur la compréhension de l'état actuel des Conseils de Développement, la cartographie des ressources existantes et l'identification des domaines clés d'opportunité.",
    phase1Activities: "Cartographie stratégique, diagnostic institutionnel, analyse des écarts, définition du périmètre de recherche",
    phase1Deliverables: "Document d'analyse stratégique, Diagnostic institutionnel, Inventaire des sources de données, Document de périmètre de recherche",
    phase1KeyMetrics: "Taux de couverture des conseils, niveau d'engagement des parties prenantes, domaines prioritaires identifiés",
    phase1Result: "Justification stratégique claire pour l'initiative de recherche",
    phase1Status: "done",
    
    // Phase 2: MEASURE
    phase2Title: "MESURER",
    phase2Period: "Octobre - Décembre 2025",
    phase2Objective: "Établir des standards de données rigoureux et des processus de vérification",
    phase2Description: "La phase de mesure met en œuvre une collecte de données rigoureuse sur plusieurs sources, établissant des métriques de référence et des indicateurs de performance.",
    phase2Activities: "Standards de qualité des données, vérification des sources, établissement de la ligne de base, implémentation du processus de validation",
    phase2Deliverables: "Standards de qualité des données, Matrice de vérification des sources, Documentation de la méthodologie YFI, 40+ points de données auditables",
    phase2KeyMetrics: "Taux de complétude des données, couverture des indicateurs, précision de validation",
    phase2Result: "40+ points de données auditables établis",
    phase2Status: "done",
    
    // Phase 3: PROMOTE
    phase3Title: "PROMOUVOIR",
    phase3Period: "Décembre 2025 - Janvier 2026",
    phase3Objective: "Communiquer la valeur et engager les parties prenantes",
    phase3Description: "La phase de promotion transforme les insights de mesure en récits convaincants qui communiquent la valeur et l'impact de l'observatoire.",
    phase3Activities: "Récit de valeur stratégique, matériels de communication, canaux d'engagement, sensibilisation des parties prenantes",
    phase3Deliverables: "Matériels multilingues, Propositions de valeur par partie prenante, Canaux d'engagement, Guides de contribution",
    phase3KeyMetrics: "Portée des parties prenantes, taux d'engagement, scores de résonance du message",
    phase3Result: "Matériels de communication multilingues opérationnels",
    phase3Status: "doing",
    
    // Phase 4: DIRECT
    phase4Title: "DIRIGER",
    phase4Period: "Janvier - Février 2026",
    phase4Objective: "Établir la gouvernance et la supervision de la qualité",
    phase4Description: "La phase de direction établit des cadres de gouvernance robustes, définit les rôles et responsabilités, et crée des structures opérationnelles durables.",
    phase4Activities: "Structure de gouvernance, processus de validation, cycles de mise à jour, définitions des responsabilités",
    phase4Deliverables: "Structure de gouvernance, Définitions des responsabilités, Checklist de qualité, Calendrier des cycles de mise à jour",
    phase4KeyMetrics: "Taux d'adoption de la gouvernance, efficacité décisionnelle, niveaux de conformité",
    phase4Result: "Structure de gouvernance avec 4 Conseils de Développement",
    phase4Status: "doing",
    
    // Phase 5: SCALE
    phase5Title: "ÉTENDRE",
    phase5Period: "Janvier 2026 - Décembre 2027+",
    phase5Objective: "Étendre la portée, l'impact et l'influence mondiale",
    phase5Description: "La phase d'extension se concentre sur l'expansion de la portée de la méthodologie, l'amplification de l'impact par les effets de réseau et l'établissement du framework comme référence mondiale.",
    phase5Activities: "Expansion à 15+ ONs, APIs publiques, partenariats académiques, influence sur les politiques",
    phase5Deliverables: "Plan de Déploiement Mondial, APIs Publiques, Partenariats académiques, Documentation d'influence sur les politiques",
    phase5KeyMetrics: "Couverture géographique, utilisateurs enregistrés, citations médiatiques",
    phase5Result: "Plateforme évoluant vers un statut d'autorité mondiale",
    phase5Status: "todo",
    
    // Data Sources Section
    dataSourcesTitle: "Sources de Données",
    dataSourcesDescription: "Données complètes provenant de sources JCI vérifiées et de recherches mondiales",
    primaryDataSourcesTitle: "Sources de Données Primaires",
    jciInstitutionalData: "Données Institutionnelles JCI",
    yfiGlobalData: "Indice des Futurs de la Jeunesse et Données Mondiales",
    secondarySourcesTitle: "Sources de Données Secondaires",
    
    // Table Column Headers
    sourceColumn: "Source",
    coverageColumn: "Couverture",
    categoryColumn: "Catégorie",
    dimensionColumn: "Dimension",
    weightColumn: "Poids",
    sourcesColumn: "Sources",
    criterionColumn: "Critère",
    exampleColumn: "Exemple",
    gapColumn: "Écart",
    mitigationColumn: "Mitigation",
    
    // Primary Data Source Items
    sourceJCIImpactReport: "Rapport d'Impact JCI 2025",
    sourceJCIVision2040: "Plan Stratégique JCI Vision 2040",
    sourceJCIMembershipSurvey: "Enquête des Membres JCI 2025",
    coverageImpactReport: "148 122 membres, 4 641 OLs",
    coverageVision2040: "Objectifs stratégiques jusqu'en 2040",
    coverageSurvey: "3 790 répondants",
    categoryJciInstitutional: "Institutionnel JCI",

    // YFI Dimensions Table
    yfiDimensionsTitle: "Dimensions de l'Indice des Futurs de la Jeunesse",
    dimensionMentalHealth: "Santé Mentale",
    dimensionWorkforce: "Emploi et Éducation",
    dimensionEntrepreneurship: "Entrepreneuriat",
    dimensionEducation: "Éducation",
    coverageCountries: "114 pays",
    sourceMentalHealth: "OMS, Sapien Labs MHQ",
    sourceWorkforce: "OIT, UNESCO, Banque Mondiale",
    sourceEntrepreneurship: "Banque Mondiale, GEM, ITU",
    
    // Formula Legend
    formulaLabel: "Formule de l'Indice des Futurs de la Jeunesse",
    formulaLegendMH: "MH = Santé Mentale",
    formulaLegendWF: "WF = Emploi et Éducation",
    formulaLegendENT: "ENT = Entrepreneuriat",
    
    // DC Partnership
    dcPartnershipDescription: "Comment les Conseils de Développement collaborent avec l'observatoire",
    dcProvidedTitle: "Ce que les CDs ont Fourni",
    dcNotProvidedTitle: "Ce que les CDs n'ont PAS Fourni",
    dcProvidedStrategic: "Collaboration stratégique, retours régionaux",
    dcProvidedRegional: "Soutien à la diffusion dans toutes les régions",
    dcProvidedValidation: "Contexte régional sur les résultats, confirmation de pertinence",
    dcProvidedPlansOfAction: "Plans d'Action 2025, stratégies régionales",
    dcNotProvidedPrimaryData: "Données de recherche primaire",
    dcNotProvidedSurveyDesign: "Conception ou exécution d'enquêtes",
    dcNotProvidedBaseline: "Collecte de données de référence",
    dcNotProvidedAnalysis: "Analyse des données",
    
    // Data Collection Section
    dataCollectionDescription: "Cinq domaines clés de collecte et d'analyse des données",
    dataCollectionJCI: "Consolidation des Données JCI",
    dataCollectionJCIDesc: "A rassemblé le Rapport d'Impact JCI 2025 officiel, extrait les données des membres, organisé par région",
    dataCollectionSurvey: "Analyse des Données d'Enquête",
    dataCollectionSurveyDesc: "A analysé 3 790 répondants pour la démographie, les motivations, les lacunes de compétences",
    dataCollectionVision: "Documentation Vision 2040",
    dataCollectionVisionDesc: "A extrait les objectifs de croissance, les piliers stratégiques, développé la planification de scénarios",
    dataCollectionYFI: "Intégration YFI",
    dataCollectionYFIDesc: "A cartographié 114 pays JCI aux classements YFI, agrégé les scores régionaux",
    dataCollectionMentalHealth: "Analyse Approfondie de la Santé Mentale",
    dataCollectionMentalHealthDesc: "A synthétisé les données de l'OMS, analysé la recherche Sapien Labs, connecté à JCI RISE",
    
    // Verification Checklist Items
    verifySourceIdentifiable: "Source identifiable",
    verifyDateConfirmed: "Date confirmée",
    verifyMethodologyDocumented: "Méthodologie documentée",
    verifySampleSizeStated: "Taille de l'échantillon indiquée",
    verifyLimitationsAcknowledged: "Limitations reconnues",
    verifyCalculationsReproducible: "Calculs reproductibles",
    
    // Zero Synthetic Data
    zeroSyntheticDescription: "L'observatoire ne contient aucune donnée synthétique",
    zeroSyntheticItem1: "Pas de projections présentées comme des faits actuels",
    zeroSyntheticItem2: "Pas de statistiques générées par modèle",
    zeroSyntheticItem3: "Pas de valeurs interpolées",
    zeroSyntheticItem4: "Pas de chiffres estimés (sauf si explicitement marqués)",
    zeroSyntheticItem5: "Pas de distributions supposées",
    
    // Acknowledged Limitations
    limitationSurveyBias: "Biais de réponse à l'enquête",
    mitigationSurveyBias: "Pondération régionale appliquée, biais documenté",
    limitationCoverage: "Lacunes de couverture des pays YFI",
    mitigationCoverage: "Moyennes régionales calculées ; lacunes notées",
    limitationLagging: "Indicateurs retardés",
    mitigationLagging: "Données les plus récentes utilisées (2024-2025)",
    limitationLanguage: "Limitations linguistiques",
    mitigationLanguage: "Traduction en 5 langues ; biais anglais noté",
    
    // Vision 2040 Alignment
    vision2040AlignmentDescription: "Comment l'observatoire soutient la vision stratégique de JCI",
    strategyWhereExpand: "Où Étendre",
    strategyWhereExpandDesc: "Expansion prioritaire en Afrique et Moyen-Orient avec des scores YFI inférieurs à 55",
    strategyWhatPrioritize: "Quoi Prioriser",
    strategyWhatPrioritizeDesc: "Dimension Santé Mentale (poids 40%) comme zone de focus principale",
    strategyHowScale: "Comment Étendre",
    strategyHowScaleDesc: "Objectif d'adoption technologique : 80%+ d'engagement numérique",
    strategySuccessMeasures: "Mesures de Succès",
    strategySuccessMeasuresDesc: "Objectif : Améliorer les scores YFI régionaux de 4-7 points d'ici 2030",
    
    // Transparency Section
    transparencyDescription: "Transparence complète de la méthodologie et reproductibilité",
    reproducibilityTitle: "Reproductibilité",
    reproducibilityDescription: "Tout chercheur peut reproduire cette analyse en utilisant les sources et la méthodologie publiées",
    noProprietaryTitle: "Pas de Méthodes Propriétaires",
    noProprietaryDescription: "Pas de calculs en boîte noire ; toutes les formules et pondérations sont documentées publiquement",
    
    // Summary Section
    summaryDescription: "Statut actuel de l'implémentation de la méthodologie de recherche",
    summaryCompletedItem1: "Phase 1 : Identifier",
    summaryCompletedItem2: "Phase 2 : Mesurer",
    summaryCompletedItem3: "Architecture des données",
    summaryCompletedItem4: "Calcul YFI",
    summaryCompletedItem5: "Couverture de 114 pays",
    summaryCompletedItem6: "Plateforme multilingue",
    summaryCompletedItem7: "Modèle de partenariat DC",
    summaryInProgressItem1: "Phase 3 : Promouvoir",
    summaryInProgressItem2: "Phase 4 : Diriger",
    summaryInProgressItem3: "Intégration des retours utilisateurs",
    summaryInProgressItem4: "Analyses régionales approfondies",
    summaryPlannedItem1: "Phase 5 : Étendre",
    summaryPlannedItem2: "Intégration UN/WEF",
    summaryPlannedItem3: "Développement d'API",
    
    // Data Integrity Section
    dataIntegrityTitle: "Normes d'Intégrité des Données",
    verificationChecklistTitle: "Liste de Vérification",
    zeroSyntheticTitle: "Engagement Zéro Données Synthétiques",
    zeroSyntheticException: "Exception : Les projections de scénarios pour la Vision 2040 sont clairement étiquetées comme 'scénarios' et illustratives, pas des prédictions.",
    acknowledgedLimitationsTitle: "Limitations Reconnues",
    
    // Verification Checklist Examples
    verifyExampleSource: "JCI Impact Report 2025, p.12",
    verifyExampleDate: "Données en date de décembre 2024",
    verifyExampleMethodology: "Méthodologie d'enquête en annexe",
    verifyExampleSampleSize: "N = 3 790 répondants",
    verifyExampleLimitations: "Biais linguistique anglais noté",
    verifyExampleCalculations: "Formule YFI publiée",
    
    // DC Partnership Section
    dcPartnershipTitle: "Partenariat avec les Conseils de Développement",
    partnershipModelTitle: "Modèle de Partenariat",
    whatDCsProvided: "Ce que les CDs ont Fourni",
    whatDCsDidNotProvide: "Ce que les CDs n'ont PAS Fourni",
    
    // Measurement Modules
    modulesTitle: "Modules de Mesure",
    modulesDescription: "Quatre domaines thématiques assurent une évaluation complète de la performance et de l'impact des Conseils de Développement.",
    
    module1Title: "Alignement Stratégique",
    module1Description: "Mesure l'alignement avec la mission, la vision et les priorités stratégiques mondiales de JCI",
    module1Indicators: "Clarté de la vision, adoption de la mission, qualité du plan stratégique, score d'alignement global",
    
    module2Title: "Excellence Opérationnelle",
    module2Description: "Évalue l'efficacité opérationnelle, la gestion des ressources et la santé organisationnelle",
    module2Indicators: "Utilisation des ressources, efficacité des processus, satisfaction des membres, durabilité financière",
    
    module3Title: "Qualité Éducative",
    module3Description: "Évalue les programmes de formation, le développement des compétences et l'efficacité du transfert de connaissances",
    module3Indicators: "Scores de qualité des programmes, résultats d'apprentissage, taux de certification, efficacité des formateurs",
    
    module4Title: "Impact et Transformation",
    module4Description: "Mesure les résultats tangibles, l'impact communautaire et le changement transformationnel",
    module4Indicators: "Taux d'achèvement des projets, portée des bénéficiaires, alignement ODD, métriques de durabilité",
    
    // Areas of Opportunity
    areasTitle: "Domaines d'Opportunité",
    areasDescription: "La méthodologie aborde quatre domaines interconnectés où les Conseils de Développement peuvent maximiser leur impact.",
    
    areaIndividual: "Développement Individuel",
    areaIndividualFocus: "Croissance personnelle et développement du leadership des membres",
    areaIndividualMetrics: "Compétences acquises, postes de leadership occupés, avancement de carrière",
    
    areaCommunity: "Impact Communautaire",
    areaCommunityFocus: "Changement positif dans les communautés locales à travers des projets et initiatives",
    areaCommunityMetrics: "Projets complétés, bénéficiaires atteints, résultats durables",
    
    areaInternational: "Connexion Internationale",
    areaInternationalFocus: "Engagement mondial et collaboration interculturelle",
    areaInternationalMetrics: "Événements internationaux, partenariats formés, participation aux échanges",
    
    areaEntrepreneurial: "Esprit Entrepreneurial",
    areaEntrepreneurialFocus: "Innovation, développement des affaires et création d'opportunités économiques",
    areaEntrepreneurialMetrics: "Entreprises soutenues, programmes d'innovation, impact économique",
    
    // Data Standards
    dataStandardsTitle: "Standards de Données",
    dataStandardsDescription: "Des standards rigoureux assurent la qualité, la fiabilité et la comparabilité des données sur toutes les sources.",
    
    acceptedTitle: "Critères de Données Acceptées",
    acceptedVerifiable: "Vérifiable via les sources officielles",
    acceptedAuditable: "Auditable avec une piste de documentation claire",
    acceptedTraceable: "Traçable jusqu'au point de collecte d'origine",
    acceptedAccurate: "Précis dans les niveaux de tolérance définis",
    acceptedCurrent: "Actuel (collecté dans le délai spécifié)",
    acceptedContextualized: "Contextualisé avec des métadonnées pertinentes",
    acceptedCited: "Correctement cité et attribué",
    
    notAcceptedTitle: "Non Accepté",
    notAcceptedSynthetic: "Données synthétiques ou générées par IA sans validation",
    notAcceptedUnverifiable: "Affirmations ou estimations non vérifiables",
    notAcceptedProprietary: "Données propriétaires sans autorisation",
    notAcceptedIncomplete: "Ensembles de données incomplets manquant de variables clés",
    notAcceptedBiased: "Échantillons biaisés non représentatifs de la population",
    
    // Validation Process
    validationTitle: "Processus de Validation",
    validationDescription: "Une validation rigoureuse en 5 étapes assure l'intégrité et la fiabilité des données.",
    
    validationStep1: "Collecte Initiale",
    validationStep1Desc: "Données collectées à l'aide d'instruments et protocoles standardisés",
    validationStep2: "Contrôle Qualité",
    validationStep2Desc: "Procédures automatisées et manuelles d'assurance qualité",
    validationStep3: "Validation Croisée",
    validationStep3Desc: "Vérification contre plusieurs sources de données",
    validationStep4: "Revue d'Experts",
    validationStep4Desc: "Évaluation et approbation par des experts du domaine",
    validationStep5: "Certification Finale",
    validationStep5Desc: "Certification officielle et approbation de publication",
    
    // Governance Structure
    governanceTitle: "Proposition de Structure de Gouvernance",
    governanceDescription: "Une gouvernance claire assure la responsabilité, la transparence et une prise de décision efficace.",
    
    governanceLevel1: "Conseil International JCI",
    governanceLevel1Desc: "Supervision stratégique et orientation politique",
    governanceLevel2: "Conseils de Développement",
    governanceLevel2Desc: "Validation des données et coordination régionale",
    governanceLevel3: "Équipe Exécutive",
    governanceLevel3Desc: "Opérations et implémentation",
    governanceLevel4: "Contributeurs et Utilisateurs",
    governanceLevel4Desc: "Retours de la communauté et engagement",
    
    // Update Cycles
    updateCyclesTitle: "Cycles de Mise à Jour",
    updateCycleAnnual: "Annuel",
    updateCycleAnnualDesc: "Actualisation complète des données (collecte Nov, validation Déc, publication Jan)",
    updateCycleIntermediate: "Intermédiaire",
    updateCycleIntermediateDesc: "Nouvelle recherche significative ou innovations de programmes",
    updateCycleContinuous: "Continu",
    updateCycleContinuousDesc: "Corrections d'erreurs, mises à jour de liens, améliorations de plateforme",
    
    // Vision 2040 Alignment
    vision2040AlignmentTitle: "Alignement avec JCI Vision 2040",
    strategicIntelligenceTitle: "Fonction d'Intelligence Stratégique",
    strategicRecommendationsTitle: "Recommandations Stratégiques",
    
    // Transparency Section
    transparencyTitle: "Transparence et Reproductibilité",
    completeSourceInventoryTitle: "Inventaire Complet des Sources",
    jciDocuments: "Documents Officiels JCI",
    globalResearchSources: "Sources de Recherche Mondiale",
    formulaReproducibility: "Tout chercheur peut reproduire cette analyse",
    formulaExplanation: "FII = (0.40 × MH) + (0.30 × WF) + (0.30 × ENT)",
    fullTransparency: "Pas de méthodologies propriétaires, pas de calculs en boîte noire, pas d'hypothèses cachées.",
    
    // Summary Section
    summaryTitle: "Statut de la Méthodologie de Recherche",
    summaryCompleted: "TERMINÉ",
    summaryInProgress: "EN COURS",
    summaryPlanned: "PLANIFIÉ",
    
    // Expansion Roadmap
    roadmapTitle: "Feuille de Route d'Expansion",
    roadmapDescription: "Une approche par phases pour l'adoption mondiale et l'amélioration continue.",
    
    roadmap2025: "2025 : Fondation",
    roadmap2025Desc: "Implémentation pilote avec 4 Conseils de Développement, standards de données établis",
    roadmap2026: "2026 : Expansion (BÊTA)",
    roadmap2026Desc: "15+ ONs contribuant, 200+ utilisateurs, APIs publiques pour chercheurs",
    roadmap2027: "2027+ : Autorité Mondiale",
    roadmap2027Desc: "Intégration UN/WEF/UNESCO, 1 000+ utilisateurs, influence sur les politiques de 10+ pays",
    
    // Success Factors
    successTitle: "Facteurs de Succès",
    successDescription: "Éléments clés qui contribuent à une mise en œuvre réussie de la méthodologie.",
    
    successFactor1: "Parrainage exécutif fort et engagement",
    successFactor2: "Communication claire et gestion du changement",
    successFactor3: "Ressources adéquates et équipes dédiées",
    successFactor4: "Approche itérative avec retours continus",
    successFactor5: "Activation technologique et automatisation",
    successFactor6: "Sensibilité culturelle et adaptation régionale",
    
    // Lessons Learned
    lessonsTitle: "Leçons Apprises",
    lessonsDescription: "Insights des implémentations pilotes et retours des parties prenantes.",
    
    lesson1: "Commencer avec des adoptants précoces volontaires pour créer l'élan",
    lesson2: "Investir massivement dans la formation et le renforcement des capacités",
    lesson3: "Équilibrer standardisation et flexibilité locale",
    lesson4: "Célébrer les victoires rapides pour maintenir l'engagement",
    lesson5: "Intégrer des mécanismes de retour dès le départ",
    
    // FAQ Section
    faqTitle: "FAQ de la Méthodologie",
    faqDescription: "Questions courantes sur la méthodologie de recherche.",
    
    faqQ1: "Quelles sources de données l'observatoire utilise-t-il ?",
    faqA1: "L'observatoire combine trois sources primaires : données institutionnelles JCI (Rapport d'Impact, Enquête des Membres, Vision 2040), l'Indice des Futurs de la Jeunesse couvrant 114 pays, et recherche mondiale de l'OMS, OIT, UNESCO, Banque Mondiale et autres sources établies.",
    
    faqQ2: "Comment la qualité des données est-elle assurée ?",
    faqA2: "Toutes les données doivent passer une liste de vérification à 7 critères : vérifiable, auditable, traçable, précis, actuel, contextualisé et correctement cité. Un processus de validation en 5 étapes assure l'intégrité avant publication.",
    
    faqQ3: "Que signifie 'zéro données synthétiques' ?",
    faqA3: "Aucune projection, statistique générée par modèle, valeur interpolée ou estimation n'est présentée comme fait. Toutes les métriques affichées sont traçables directement à des sources vérifiées. La seule exception sont les scénarios Vision 2040, clairement étiquetés comme illustratifs.",
    
    faqQ4: "À quelle fréquence les données sont-elles mises à jour ?",
    faqA4: "L'observatoire suit trois cycles de mise à jour : actualisation complète annuelle (Nov-Jan), mises à jour intermédiaires pour nouvelle recherche significative, et améliorations continues pour corrections et mises à jour de plateforme.",
    
    faqQ5: "Les chercheurs peuvent-ils reproduire l'analyse ?",
    faqA5: "Oui. Tous les documents sources sont publiquement disponibles, la logique d'agrégation est documentée et la formule FII (0.40×MH + 0.30×WF + 0.30×ENT) est transparente. Tout chercheur peut reproduire et vérifier les résultats.",
    
    // CTA Section
    ctaTitle: "Prêt à Explorer l'Observatoire ?",
    ctaDescription: "Accédez à des insights basés sur les preuves pour soutenir les décisions stratégiques de votre organisation.",
    ctaButton1: "Explorer le Tableau de Bord",
    ctaButton2: "Comment Contribuer",
    ctaButton3: "Lire la FAQ",
    
    // Source Attribution
    sourceTitle: "Source",
    sourceDescription: "Méthodologie Version 3.1, Janvier 2026",
    sourceNote: "Développé par les Conseils de Développement JCI en collaboration avec les parties prenantes mondiales"
  }
} as const;
